import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { fetchAvailableStores } from "../services/api";
import { Mail, ArrowLeft, Lock, CheckCircle2, Store, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";

type LoginStep = "email" | "otp";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDevLogin = location.pathname === '/logindev';
  const {
    sendOTP,
    verifyOTP,
    selectStore,
    isLoading,
    isWakingUp,
    wakeUpMessage,
    requiresStoreSelection,
    setUseDevBranch,
    user
  } = useAuthStore();

  // Definir flag useDevBranch quando estiver em /logindev
  useEffect(() => {
    console.log(`🔍 LoginPage: isDevLogin = ${isDevLogin}, definindo useDevBranch = ${isDevLogin}`)
    setUseDevBranch(isDevLogin);
  }, [isDevLogin, setUseDevBranch]);

  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [storeName, setStoreName] = useState(""); // Nome da loja selecionada
  const [availableStores, setAvailableStores] = useState<{ storename: string; api_base_url: string; preview_base_url: string }[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);

  // Multi-loja (2+ lojas, sem 'multistore'): o select mostra apenas as lojas do perfil.
  // Multistore: mostra todas as lojas ativas.
  const allowedStores = user?.allowedStores || [];
  const isRestrictedList = allowedStores.length > 0 && !allowedStores.includes("multistore");
  const visibleStores = isRestrictedList
    ? availableStores.filter((s) => allowedStores.includes(s.storename.toLowerCase()))
    : availableStores;
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [canResendOTP, setCanResendOTP] = useState(true);

  // Gerenciar countdown de 60 segundos
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (cooldownSeconds > 0) {
      setCanResendOTP(false);
      interval = setInterval(() => {
        setCooldownSeconds((seconds) => {
          if (seconds <= 1) {
            setCanResendOTP(true);
            return 0;
          }
          return seconds - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownSeconds]);

  // Redirecionar quando autenticado e não-multistore
  useEffect(() => {
    // This effect is no longer needed as StoreSelectionModal is removed.
    // The user will be redirected to '/' if they are not multistore.
  }, []);

  // Carregar stores disponíveis quando necessário
  useEffect(() => {
    const loadStores = async () => {
      if (requiresStoreSelection && availableStores.length === 0) {
        setLoadingStores(true);
        try {
          const stores = await fetchAvailableStores();
          setAvailableStores(stores);
          if (stores.length === 0) {
            toast.error('Nenhuma loja encontrada no sistema');
          }
        } catch (error) {
          console.error('Erro ao carregar stores:', error);
          toast.error('Erro ao carregar lista de lojas');
        } finally {
          setLoadingStores(false);
        }
      }
    };

    loadStores();
  }, [requiresStoreSelection, availableStores.length]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error("Por favor, digite seu e-mail");
      return;
    }

    if (!canResendOTP) {
      toast.error(
        `Aguarde ${cooldownSeconds} segundos para solicitar outro código`
      );
      return;
    }

    try {
      await sendOTP(email);
      toast.success("Código de acesso enviado para seu e-mail!");
      setStep("otp");
      // Iniciar countdown de 60 segundos
      setCooldownSeconds(60);
    } catch (error: any) {
      console.error("Erro ao enviar OTP:", error);
      if (error.message?.includes("User not found")) {
        toast.error(
          "E-mail não encontrado. Verifique se você tem acesso ao sistema."
        );
      } else {
        toast.error("Erro ao enviar código. Tente novamente.");
      }
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode || otpCode.length !== 6) {
      toast.error("Por favor, digite o código de 6 dígitos");
      return;
    }

    try {
      await verifyOTP(email, otpCode); // Não envia storeName
      
      // Obter o estado atualizado diretamente do store após verifyOTP
      const { requiresStoreSelection: currentRequiresStoreSelection } = useAuthStore.getState();
      
      // Se requiresStoreSelection ainda for true após verifyOTP, 
      // significa que o usuário é multistore e precisa selecionar uma store
      if (currentRequiresStoreSelection) {
        // Não navegar ainda, continuar na tela de seleção
        return;
      }
      toast.success("Login bem-sucedido!");
      navigate("/");
    } catch (error: any) {
      console.error("Erro ao verificar OTP:", error);
      if (error.message?.includes("Token has expired")) {
        toast.error("Código expirado ou inválido. Solicite um novo código.");
      } else if (error.message?.includes("Invalid token")) {
        toast.error("Código expirado ou inválido. Solicite um novo código.");
      } else {
        toast.error("Erro ao verificar código. Tente novamente.");
      }
    }
  };

  const handleSelectStore = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!storeName) {
      toast.error("Por favor, selecione uma loja para acessar.");
      return;
    }

    try {
      await selectStore(storeName);
      toast.success("Login bem-sucedido!");
      navigate("/");
    } catch (error: any) {
      console.error("Erro ao selecionar loja:", error);
      toast.error("Erro ao selecionar loja. Tente novamente.");
    }
  };

  const handleResendOTP = async () => {
    if (!canResendOTP) {
      toast.error(
        `Aguarde ${cooldownSeconds} segundos para solicitar outro código`
      );
      return;
    }

    try {
      await sendOTP(email);
      toast.success("Novo código enviado para seu e-mail!");
      setOtpCode(""); // Limpar código anterior
      // Iniciar countdown de 60 segundos
      setCooldownSeconds(60);
    } catch (error: any) {
      console.error("Erro ao reenviar OTP:", error);
      if (error.message?.includes("User not found")) {
        toast.error(
          "E-mail não encontrado. Verifique se você tem acesso ao sistema."
        );
      } else {
        toast.error("Erro ao reenviar código. Tente novamente.");
      }
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setOtpCode("");
    // Manter o cooldown ativo quando volta para tela de email
  };

  // ---- Lógica de Renderização ----

  // Caso 1: Usuário é multistore, mostrar modal
  // This case is no longer relevant as StoreSelectionModal is removed.
  // The user will be redirected to '/' if they are not multistore.

  // Caso 2: Passo de inserir email
  if (step === "email") {
    // Tela 1: Solicitar e-mail para envio do código
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-md shadow-lg p-8">
            {/* Logo */}
            <div className="flex flex-col items-center">
              <img
                src="/logo.png"
                alt="Site Editor Wicomm"
                className="h-8 w-auto"
              />

              <h1 className="text-xs text-gray-900">Site Editor</h1>
              
              {/* Dev Mode Badge */}
              {isDevLogin && (
                <span className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  DEV MODE
                </span>
              )}
            </div>

            {/* Título e descrição */}
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Acesse sua conta
              </h2>
              <p className="text-gray-600">
                Digite seu email para receber o código de acesso
              </p>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSendOTP} className="space-y-6">
              {isWakingUp && wakeUpMessage && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  {wakeUpMessage}
                </div>
              )}

              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-primary-200 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="seu@email.com.br"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Um código será enviado para este email
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || isWakingUp || !canResendOTP}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="h-4 w-4 mr-2" />
                {isLoading
                  ? "Enviando código de acesso..."
                  : isWakingUp
                  ? "Iniciando ambiente..."
                  : !canResendOTP
                  ? `Aguarde ${cooldownSeconds}s para reenviar`
                  : "Enviar código de acesso"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Caso 3: Passo de inserir OTP
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-md shadow-lg p-8">
          {/* Header com botão voltar */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handleBackToEmail}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
            </button>
            <div className="flex flex-col items-center mb-8">
              <img
                src="/logo.png"
                alt="Site Editor Wicomm"
                className="h-8 w-auto"
              />

              <h1 className="text-xs text-gray-900">Site Editor</h1>
              
              {/* Dev Mode Badge */}
              {isDevLogin && (
                <span className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  DEV MODE
                </span>
              )}
            </div>
            <div className="w-6"></div> {/* Spacer */}
          </div>

          {/* Título e descrição */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Confirme o código
            </h2>
            <p className="text-gray-600 mb-1">Código enviado para</p>
            <p className="font-medium text-gray-900">{email}</p>
          </div>

          {/* Status de sucesso */}
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex items-start">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Código de acesso enviado para seu email.
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Verifique sua caixa de entrada e spam.
                </p>
              </div>
            </div>
          </div>

          {/* Formulário de código */}
          <form onSubmit={requiresStoreSelection ? handleSelectStore : handleVerifyOTP} className="space-y-6">
            {isWakingUp && wakeUpMessage && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                {wakeUpMessage}
              </div>
            )}

            {/* Campo de OTP - oculto quando já validado e precisa selecionar store */}
            {!requiresStoreSelection && (
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) =>
                      setOtpCode(e.target.value.replace(/\D/g, ""))
                    }
                    className="block w-full pl-3 pr-3 py-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="000000"
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray-600">
                    Verifique sua caixa de entrada e spam
                  </p>
                  <p className="text-sm text-gray-500">6 dígitos</p>
                </div>
              </div>
            )}

            {/* Campo Condicional para Store Selection */}
            {requiresStoreSelection && (
              <div>
                <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-1">
                  Selecione a Loja
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Store className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="storeName"
                    required
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    disabled={loadingStores}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">
                      {loadingStores ? "Carregando lojas..." : "Selecione uma loja"}
                    </option>
                    {visibleStores.map((store) => (
                      <option key={store.storename} value={store.storename}>
                        {store.storename}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Seu acesso é de desenvolvedor. Selecione a loja que deseja editar.
                </p>
                {loadingStores && (
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
                    Carregando lojas disponíveis...
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={
                isLoading || 
                isWakingUp ||
                (!requiresStoreSelection && otpCode.length !== 6) || 
                (requiresStoreSelection && !storeName)
              }
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Lock className="h-4 w-4 mr-2" />
              {isLoading ? "Verificando..." : requiresStoreSelection ? "Acessar loja selecionada" : "Entrar no sistema"}
            </button>

            <div className="space-y-2">
              {/* Botão reenviar código - oculto quando já validado */}
              {!requiresStoreSelection && (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isLoading || !canResendOTP}
                  className="w-full flex justify-center items-center py-2 px-4 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {!canResendOTP
                    ? `Reenviar código em ${cooldownSeconds}s`
                    : "Reenviar código"}
                </button>
              )}

              <button
                type="button"
                onClick={handleBackToEmail}
                className="w-full flex justify-center items-center py-2 px-4 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para email
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
